import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import {
  approvedPhotos, buildTeamNames, groupByTeam, groupByTime, videosOf,
  rankHighlights, photoPermalink, filterBySource, collapseDuplicates,
} from '../lib/photoCuration.js'
import { getFavorites, getLiked, toggleFavorite, toggleLiked } from '../lib/photoLocal.js'
import { likePhoto } from '../lib/data.js'

// Interactive, tabbed photo browse (phone + hub). Mounted at /photowall.
//   Recent · Highlights · By Time · Teams · Videos · Favorites.
//   Highlights is a PURELY SHARED ranking (likes + featured + decay).
//   Favorites is device-local (localStorage). The passive TV slideshow is
//   PhotoWallApp.
const TABS = [
  { key: 'recent',     label: 'Recent' },
  { key: 'highlights', label: 'Highlights' },
  { key: 'time',       label: 'By Time' },
  { key: 'teams',      label: 'Teams' },
  { key: 'videos',     label: 'Videos' },
  { key: 'favorites',  label: 'Favorites' },
]

export default function PhotoBrowse() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')
  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('recent')
  const [team, setTeam] = useState(null)   // drilled-into team group
  const [source, setSource] = useState('all') // all | player | staff | guest
  const [lightbox, setLightbox] = useState(null) // { list, index }

  useEffect(() => {
    if (!tournamentId) { setError('Missing tournament ID.'); return }
    const off = onValue(ref(db, `tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  const [favs, setFavs] = useState([])
  const [liked, setLiked] = useState([])
  useEffect(() => {
    if (!tournamentId) return
    setFavs(getFavorites(tournamentId))
    setLiked(getLiked(tournamentId))
  }, [tournamentId])

  const allPhotos = useMemo(() => approvedPhotos(tournament?.photos), [tournament])
  const photos = useMemo(() => filterBySource(allPhotos, source), [allPhotos, source])
  const teamNames = useMemo(() => buildTeamNames(tournament?.flights), [tournament])
  const waveLabeler = useMemo(() => buildWaveLabeler(tournament?.teeTimes), [tournament])
  const highlights = useMemo(() => rankHighlights(photos, { now: Date.now() }), [photos])
  const recentDeduped = useMemo(() => collapseDuplicates(photos, { windowMs: 10_000 }), [photos])
  // Favorites stay across sources (personal), from the full approved set.
  const favPhotos = useMemo(() => allPhotos.filter((p) => favs.includes(p.id)), [allPhotos, favs])

  const openLightbox = (list, index) => setLightbox({ list, index })

  // Device-local toggles. Like also nudges the SHARED likeCount ±1.
  const onFav = (p) => setFavs(toggleFavorite(tournamentId, p.id))
  const onLike = (p) => {
    const wasLiked = liked.includes(p.id)
    setLiked(toggleLiked(tournamentId, p.id))
    likePhoto(tournamentId, p.id, wasLiked ? -1 : 1).catch(() => {})
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0A1710' }}>
      {/* Top bar */}
      <div className="sticky top-0 z-20 flex items-center justify-between gap-2 px-3 py-3"
           style={{ backgroundColor: CLUB.primaryColor, borderBottom: `1px solid ${CLUB.accentColor}44` }}>
        <a href={tournamentId ? `/hub?t=${tournamentId}` : '/'}
           className="rounded-lg px-2.5 py-1.5 text-sm font-extrabold uppercase tracking-wider active:scale-95"
           style={{ color: CLUB.accentColor, border: `1px solid ${CLUB.accentColor}66` }}>← Back</a>
        <span className="font-extrabold uppercase tracking-widest text-sm" style={{ color: CLUB.accentColor }}>📸 Photos</span>
        <a href={tournamentId ? `/slideshow?t=${tournamentId}` : '#'}
           className="rounded-lg px-2.5 py-1.5 text-sm font-extrabold uppercase tracking-wider active:scale-95"
           style={{ backgroundColor: CLUB.accentColor, color: CLUB.primaryColor }}>▶ Play</a>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto px-3 py-2" style={{ borderBottom: `1px solid ${CLUB.accentColor}22` }}>
        {TABS.map((t) => {
          const active = tab === t.key
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setTeam(null) }}
              className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-bold uppercase tracking-wider transition"
              style={active
                ? { backgroundColor: CLUB.accentColor, color: CLUB.primaryColor }
                : { backgroundColor: 'transparent', color: `${CLUB.accentColor}cc`, border: `1px solid ${CLUB.accentColor}44` }}>
              {t.label}
            </button>
          )
        })}
      </div>

      <div className="max-w-3xl mx-auto px-3 py-4">
        {tournament && allPhotos.length > 0 && tab !== 'favorites' ? (
          <SourceFilter source={source} onChange={setSource} />
        ) : null}
        {error ? (
          <p className="text-center text-white/70 mt-10">{error}</p>
        ) : !tournament ? (
          <p className="text-center text-white/60 mt-10">Loading…</p>
        ) : allPhotos.length === 0 ? (
          <EmptyState />
        ) : photos.length === 0 && tab !== 'favorites' ? (
          <p className="text-center text-white/50 mt-10">No photos from this source yet.</p>
        ) : tab === 'recent' ? (
          <Grid items={recentDeduped} onOpen={(i) => openLightbox(recentDeduped, i)} />
        ) : tab === 'highlights' ? (
          <Grid items={highlights} onOpen={(i) => openLightbox(highlights, i)} />
        ) : tab === 'time' ? (
          <TimeView photos={photos} waveLabeler={waveLabeler} onOpen={openLightbox} />
        ) : tab === 'teams' ? (
          team
            ? <TeamDrill group={team} onBack={() => setTeam(null)} onOpen={(i) => openLightbox(team.photos, i)} />
            : <TeamsView photos={photos} teamNames={teamNames} onPick={setTeam} />
        ) : tab === 'videos' ? (
          <VideosView photos={photos} onOpen={openLightbox} />
        ) : favPhotos.length === 0 ? (
          <p className="text-center text-white/50 mt-10">No favorites yet — tap ☆ on a photo to save it here (this device only).</p>
        ) : (
          <Grid items={favPhotos} onOpen={(i) => openLightbox(favPhotos, i)} />
        )}
      </div>

      {lightbox ? (
        <Lightbox
          list={lightbox.list}
          index={lightbox.index}
          teamNames={teamNames}
          tournamentId={tournamentId}
          likedSet={liked}
          favSet={favs}
          onLike={onLike}
          onFav={onFav}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => ({ ...lb, index: i }))}
        />
      ) : null}
    </div>
  )
}

const SOURCES = [
  { key: 'all',    label: 'All' },
  { key: 'player', label: 'Players' },
  { key: 'staff',  label: 'Staff' },
  { key: 'guest',  label: 'Guests' },
]
function SourceFilter({ source, onChange }) {
  return (
    <div className="flex gap-1 mb-3">
      {SOURCES.map((s) => {
        const active = source === s.key
        return (
          <button key={s.key} onClick={() => onChange(s.key)}
            className="rounded-full px-3 py-1 text-sm font-bold uppercase tracking-wider transition"
            style={active
              ? { backgroundColor: '#ffffff', color: '#0A1710' }
              : { backgroundColor: '#ffffff14', color: '#ffffffcc' }}>
            {s.label}
          </button>
        )
      })}
    </div>
  )
}

// AI Winner of the Hour → badge icon + ring color (top photo gold, top video blue).
const CROWN = {
  photo: { icon: '🏆', ring: '#F5C542', label: 'Top Photo' },
  video: { icon: '🎬', ring: '#7FD1FF', label: 'Top Video' },
}

// ── Thumbnail grid ──────────────────────────────────────────────────────────
// `crowns` (optional Map id→'photo'|'video') marks the hour's AI winners.
function Grid({ items, onOpen, crowns = null }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
      {items.map((p, i) => {
        const crown = crowns?.get(p.id) || null
        return (
        <button key={p.id} onClick={() => onOpen(i)}
          className="relative aspect-square overflow-hidden rounded-md bg-black active:scale-[0.98]"
          style={crown ? { boxShadow: `0 0 0 2px ${CROWN[crown].ring}` } : undefined}>
          {p.type === 'video' ? (
            p.posterUrl ? (
              <>
                <img src={p.posterUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center text-2xl" style={{ color: '#fff', textShadow: '0 1px 4px #000' }}>▶</span>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#111' }}>
                <span className="text-2xl" style={{ color: CLUB.accentColor }}>▶</span>
              </div>
            )
          ) : (
            <img src={p.url} alt={p.caption || ''} loading="lazy" className="w-full h-full object-cover" />
          )}
          {crown ? (
            <span className="absolute top-1 left-1 text-sm">{CROWN[crown].icon}</span>
          ) : p.featured ? <span className="absolute top-1 left-1 text-sm" style={{ color: CLUB.accentColor }}>★</span> : null}
          {p.dupCount > 1 ? <span className="absolute top-1 right-1 text-sm font-bold px-1 rounded bg-black/60 text-white">+{p.dupCount - 1}</span> : null}
          {Number(p.likeCount) > 0 ? (
            <span className="absolute bottom-1 right-1 text-sm font-bold px-1 rounded bg-black/60 text-white">♥ {p.likeCount}</span>
          ) : null}
        </button>
        )
      })}
    </div>
  )
}

function SectionHeader({ title, sub, count }) {
  return (
    <div className="flex items-baseline justify-between mt-4 mb-2">
      <div>
        <span className="font-extrabold" style={{ color: CLUB.accentColor }}>{title}</span>
        {sub ? <span className="ml-2 text-sm text-white/60">{sub}</span> : null}
      </div>
      <span className="text-sm text-white/50">{count}</span>
    </div>
  )
}

// ── By Time ─────────────────────────────────────────────────────────────────
// Photos auto-group into hourly batches (groupByTime). Pick ONE hour so the
// flood stays manageable; each hour crowns TWO AI winners — top photo + top
// video by aiInterest (no voting). Selection tracks the hour's start so a new
// hour arriving doesn't yank the view — default is the newest hour.
function TimeView({ photos, waveLabeler, onOpen }) {
  const blocks = useMemo(() => groupByTime(photos, { waveLabeler }), [photos, waveLabeler])
  const [pickStart, setPickStart] = useState(null)
  const sel = blocks.find((b) => b.start === pickStart) || blocks[0] || null
  if (!sel) return null
  const winners = [
    sel.topPhoto ? { kind: 'photo', p: sel.topPhoto } : null,
    sel.topVideo ? { kind: 'video', p: sel.topVideo } : null,
  ].filter(Boolean)
  const crowns = new Map(winners.map((w) => [w.p.id, w.kind])) // id → 'photo'|'video'
  const openInHour = (p) => onOpen(sel.photos, sel.photos.findIndex((x) => x.id === p.id))
  return (
    <div>
      {/* Hour selector — one chip per hourly batch (label · count). */}
      <div className="flex gap-1 overflow-x-auto pb-2 mb-2">
        {blocks.map((b) => {
          const active = b.start === sel.start
          return (
            <button key={b.start} onClick={() => setPickStart(b.start)}
              className="whitespace-nowrap rounded-full px-3 py-1 text-sm font-bold uppercase tracking-wider transition"
              style={active
                ? { backgroundColor: CLUB.accentColor, color: CLUB.primaryColor }
                : { backgroundColor: 'transparent', color: `${CLUB.accentColor}cc`, border: `1px solid ${CLUB.accentColor}44` }}>
              {b.label} · {b.count}
            </button>
          )
        })}
      </div>

      {/* AI Winners of the selected hour — top photo + top video (either may be absent). */}
      {winners.length > 0 ? (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-extrabold uppercase tracking-widest" style={{ color: '#F5C542' }}>🏆 Winners of the Hour</span>
            <span className="text-sm text-white/50">{sel.label}</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {winners.map(({ kind, p }) => (
              <button key={p.id} onClick={() => openInHour(p)}
                className="relative aspect-[4/3] overflow-hidden rounded-lg bg-black active:scale-[0.98]"
                style={{ boxShadow: `0 0 0 2px ${CROWN[kind].ring}` }}>
                {p.type === 'video'
                  ? (p.posterUrl
                      ? <img src={p.posterUrl} alt="" className="w-full h-full object-cover opacity-90" />
                      : <div className="w-full h-full" style={{ backgroundColor: '#111' }} />)
                  : <img src={p.url} alt={p.caption || ''} className="w-full h-full object-cover opacity-90" />}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.8), rgba(0,0,0,0))' }} />
                <span className="absolute top-1.5 left-1.5 text-lg">{CROWN[kind].icon}</span>
                <span className="absolute bottom-1.5 left-2 right-2 text-sm font-extrabold uppercase tracking-wider" style={{ color: CROWN[kind].ring }}>
                  {CROWN[kind].label}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <SectionHeader title={sel.label} sub={sel.waveLabel || ''} count={`${sel.count} photo${sel.count === 1 ? '' : 's'}`} />
      <Grid items={sel.photos} crowns={crowns} onOpen={(i) => onOpen(sel.photos, i)} />
    </div>
  )
}

// ── Teams ───────────────────────────────────────────────────────────────────
function TeamsView({ photos, teamNames, onPick }) {
  const groups = useMemo(() => groupByTeam(photos, teamNames), [photos, teamNames])
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {groups.map((g) => (
        <button key={g.teamId || 'gallery'} onClick={() => onPick(g)}
          className="relative aspect-[4/3] overflow-hidden rounded-lg bg-black active:scale-[0.98] text-left">
          {g.cover && g.cover.type !== 'video' ? (
            <img src={g.cover.url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          ) : <div className="absolute inset-0" style={{ backgroundColor: '#111' }} />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85), rgba(0,0,0,0.1))' }} />
          <div className="absolute bottom-0 left-0 right-0 p-2.5">
            <div className="font-extrabold text-sm leading-tight">{g.teamName}</div>
            <div className="text-sm text-white/70">{g.count} photo{g.count === 1 ? '' : 's'}</div>
          </div>
        </button>
      ))}
    </div>
  )
}

function TeamDrill({ group, onBack, onOpen }) {
  return (
    <div>
      <button onClick={onBack} className="text-sm font-bold uppercase tracking-wider mb-1" style={{ color: CLUB.accentColor }}>← All teams</button>
      <SectionHeader title={group.teamName} count={`${group.count} photo${group.count === 1 ? '' : 's'}`} />
      <Grid items={group.photos} onOpen={onOpen} />
    </div>
  )
}

// ── Videos ──────────────────────────────────────────────────────────────────
function VideosView({ photos, onOpen }) {
  const vids = useMemo(() => videosOf(photos), [photos])
  if (vids.length === 0) return <p className="text-center text-white/50 mt-10">No videos yet.</p>
  return <Grid items={vids} onOpen={(i) => onOpen(vids, i)} />
}

// ── Lightbox ────────────────────────────────────────────────────────────────
function Lightbox({ list, index, teamNames, tournamentId, likedSet, favSet, onLike, onFav, onClose, onIndex }) {
  const p = list[index]
  if (!p) return null
  const teamName = p.teamId ? (teamNames[p.teamId] || null) : null
  const prev = () => onIndex((index - 1 + list.length) % list.length)
  const next = () => onIndex((index + 1) % list.length)
  const isLiked = (likedSet || []).includes(p.id)
  const isFav = (favSet || []).includes(p.id)
  const likeCount = Math.max(0, Number(p.likeCount) || 0)
  const share = async () => {
    const url = `${window.location.origin}${photoPermalink(tournamentId, p.id)}`
    try {
      if (navigator.share) await navigator.share({ url, title: 'Tournament photo' })
      else { await navigator.clipboard?.writeText(url); alert('Link copied') }
    } catch { /* cancelled */ }
  }
  const pill = (active) => (active
    ? { backgroundColor: CLUB.accentColor, color: CLUB.primaryColor }
    : { backgroundColor: '#ffffff18', color: '#fff' })
  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: 'rgba(0,0,0,0.94)' }} onClick={onClose}>
      <div className="flex justify-between items-center px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <span className="text-white/70 text-sm">{index + 1} / {list.length}</span>
        <button onClick={onClose} className="text-white/80 text-xl px-2">✕</button>
      </div>
      <div className="flex-1 flex items-center justify-center px-2" onClick={(e) => e.stopPropagation()}>
        {p.type === 'video'
          ? <video src={p.url} poster={p.posterUrl || undefined} controls autoPlay playsInline className="max-w-full max-h-full object-contain" />
          : <img src={p.url} alt={p.caption || ''} className="max-w-full max-h-full object-contain" />}
      </div>
      <div className="px-5 py-3 text-white" onClick={(e) => e.stopPropagation()}>
        {p.caption ? <div className="font-bold">{p.caption}</div> : null}
        <div className="text-sm text-white/70 flex flex-wrap gap-2 mt-1">
          <span>{p.submittedBy || 'Guest'}</span>
          {teamName ? <span>· {teamName}</span> : null}
          {p.hole != null ? <span>· Hole {p.hole}</span> : null}
          <span>· {formatTime(p.timestamp)}</span>
        </div>
      </div>
      <div className="flex gap-2 px-4 pb-3" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onLike(p)} className="rounded-lg px-4 py-2 text-sm font-bold" style={pill(isLiked)}>♥ {likeCount}</button>
        <button onClick={() => onFav(p)} className="rounded-lg px-4 py-2 text-sm font-bold" style={pill(isFav)}>{isFav ? '★ Saved' : '☆ Save'}</button>
        <button onClick={share} className="rounded-lg px-4 py-2 text-sm font-bold" style={pill(false)}>⤴ Share</button>
      </div>
      <div className="flex justify-between px-4 pb-6" onClick={(e) => e.stopPropagation()}>
        <button onClick={prev} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ backgroundColor: `${CLUB.accentColor}22`, color: CLUB.accentColor }}>‹ Prev</button>
        <button onClick={next} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ backgroundColor: `${CLUB.accentColor}22`, color: CLUB.accentColor }}>Next ›</button>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="text-center mt-16 text-white/60">
      <div className="text-4xl mb-3">📸</div>
      <p>Photos will appear here as they’re approved.</p>
    </div>
  )
}

function formatTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// Soft wave labeler: maps a photo time-block to a tee-time wave when the block
// hour matches a wave's start hour. Best-effort; returns null when no match.
function buildWaveLabeler(teeTimes) {
  const byHour = {}
  for (const day of Object.values(teeTimes || {})) {
    const waves = day?.waves
    if (!waves) continue
    let n = 0
    for (const w of Object.values(waves)) {
      n++
      const h = parseHour(w?.startTime)
      if (h != null) byHour[h] = w?.label || `Wave ${n}`
    }
  }
  if (Object.keys(byHour).length === 0) return null
  return (blockStart) => byHour[new Date(blockStart).getHours()] || null
}

function parseHour(str) {
  const m = String(str || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
  if (!m) return null
  let h = Number(m[1]) % 12
  if (/pm/i.test(m[3] || '')) h += 12
  return h
}
